import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  searchApi,
  speciesSearchApi,
  trackEvent,
  type SearchFilters,
  type SearchResponse,
  type SpeciesRef,
} from '../api';
import { toast } from '../toast';
import { isSubmitEnter } from '../keyboard';
import UiIcon from '../UiIcon';
import { imageLink } from '../lib/imageUrl';
import ScientificText from '../ScientificText';
import { readMicrophonePermission, requestMicrophoneAccess, voiceErrorMessage } from '../lib/voiceSearch';
import './Search.css';

// 名称 → 图标的本地映射；只影响视觉呈现，不作为图鉴或检索结果数据源。
const SPECIES_ICON_HINTS = [
  { name: '草鱼',   sub: '淡水', icon: '🐟' },
  { name: '鲤鱼',   sub: '淡水', icon: '🐠' },
  { name: '鳙鱼',   sub: '淡水', icon: '🐟' },
  { name: '鲢鱼',   sub: '淡水', icon: '🐟' },
  { name: '青鱼',   sub: '淡水', icon: '🐠' },
  { name: '鲫鱼',   sub: '淡水', icon: '🐟' },
  { name: '鲈鱼',   sub: '淡/海', icon: '🐠' },
  { name: '黄颡鱼', sub: '淡水', icon: '🐟' },
  { name: '江团',   sub: '淡水', icon: '🐡' },
  { name: '鳜鱼',   sub: '淡水', icon: '🐠' },
  { name: '罗非鱼', sub: '淡水', icon: '🐟' },
  { name: '带鱼',   sub: '海水', icon: '🐟' },
  { name: '黄花鱼', sub: '海水', icon: '🐠' },
  { name: '鲅鱼',   sub: '海水', icon: '🐟' },
  { name: '鲳鱼',   sub: '海水', icon: '🐠' },
  { name: '金枪鱼', sub: '海水', icon: '🐟' },
  { name: '小龙虾', sub: '甲壳', icon: '🦞' },
  { name: '对虾',   sub: '甲壳', icon: '🦐' },
  { name: '基围虾', sub: '甲壳', icon: '🦐' },
  { name: '青蟹',   sub: '甲壳', icon: '🦀' },
  { name: '梭子蟹', sub: '甲壳', icon: '🦀' },
  { name: '鱿鱼',   sub: '头足', icon: '🦑' },
  { name: '章鱼',   sub: '头足', icon: '🐙' },
  { name: '扇贝',   sub: '贝类', icon: '🐚' },
];

type FilterKey = 'category' | 'region' | 'production_area' | 'waterway' | 'feature' | 'habit';

type WebSpeechRecognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onstart: (() => void) | null;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type WebSpeechRecognitionConstructor = new () => WebSpeechRecognition;

const SEARCH_FILTERS: Record<FilterKey, string[]> = {
  category: ['全部', '淡水鱼', '海水鱼', '甲壳类', '头足类', '贝类'],
  region:   ['全部', '华东', '华南', '华中', '华北', '西南', '东北', '西北'],
  production_area: ['全部', '江苏', '浙江', '福建', '山东', '广东', '湖北', '安徽', '四川'],
  waterway: ['全部', '长江', '黄河', '珠江', '黑龙江', '东海', '南海', '黄海', '渤海'],
  feature:  ['全部', '长吻', '无鳞', '银白体侧', '青背白肚', '红色外壳', '黑色横斑'],
  habit:    ['全部', '植食性', '肉食性', '夜行性', '海水', '咸淡水', '养殖', '捕捞'],
};
const FILTER_LABELS: Record<FilterKey, { text: string; color: string }> = {
  category: { text: '分类', color: '#1aa8c9' },
  region:   { text: '地域', color: '#ff8e3c' },
  production_area: { text: '产地', color: '#e16a3d' },
  waterway: { text: '水系', color: '#8a6cff' },
  feature:  { text: '特征', color: '#df638a' },
  habit:    { text: '习性', color: '#4c9c69' },
};


const SUGGESTED_QUESTIONS = [
  '小龙虾生活在哪些水系？',
  '江团和鲶鱼的区别是什么？',
  '家庭如何判断带鱼是否新鲜？',
  '青鱼主要分布在哪里？',
  '鳜鱼为什么这么贵？',
  '对虾和基围虾怎么区分？',
];

type Result = {
  id: string;
  ribbon?: string;
  thumb: string;
  title: string;
  meta: string[];
  excerpt: string;
  cite: string;
  original: string;
  focused: boolean;
  bookId?: string;
  bookTitle?: string;
  page?: number;
};

function SearchImage({ image }: { image: { url: string; caption?: string } }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image.url]);
  if (failed) return <div className="matched-image-fallback" role="img" aria-label="图片暂不可用">🖼️</div>;
  const picture = <img src={image.url} alt={image.caption ?? '搜索匹配图片'} loading="lazy" onError={() => setFailed(true)} />;
  const href = imageLink(image.url);
  return href ? <a href={href} target="_blank" rel="noopener noreferrer" title="点击查看原图" aria-label={`查看原图：${image.caption ?? '搜索匹配图片'}`}>{picture}</a> : picture;
}

function pickEmoji(name: string): string {
  const m = SPECIES_ICON_HINTS.find((s) => name.includes(s.name));
  return m?.icon ?? '🐟';
}

function normalizeCategory(category: string): string {
  const mapping: Record<string, string> = {
    淡水: '淡水鱼',
    淡水鱼类: '淡水鱼',
    海水: '海水鱼',
    海水鱼类: '海水鱼',
    甲壳: '甲壳类',
    虾蟹类: '甲壳类',
    头足: '头足类',
  };
  return mapping[category] ?? category;
}

// 将 SearchResponse 转成 result-card 用的视图模型
function toResultCards(r: SearchResponse): Result[] {
  const out: Result[] = [];
  for (let i = 0; i < r.chunks.length; i++) {
    const c = r.chunks[i]!;
    const excerpt = r.excerpts?.find((item) => item.chunk_id === c.id);
    out.push({
      id: c.id,
      thumb: pickEmoji(c.book_title || c.text),
      title: excerpt?.title || c.chapter_title || c.book_title || c.text.slice(0, 24),
      meta: [c.book_title, c.chapter_title ?? '', c.page != null ? `P.${c.page}` : ''].filter(Boolean),
      excerpt: excerpt?.text || c.text.slice(0, 200),
      original: c.text,
      focused: excerpt?.focused ?? false,
      cite: `📘 《${c.book_title}》${c.page != null ? ' · P.' + c.page : ''}`,
      bookId: c.book_id,
      bookTitle: c.book_title,
      page: c.page ?? undefined,
    });
  }
  return out;
}

// 推荐顺序由后端综合实体依据和检索排序给出，融合分不是置信度。
// 不再用"最新/权威度"——检索结果无可靠时间/权威字段，避免假排序。
type SortBy = 'rel' | 'book' | 'page';
const SORTS: { key: SortBy; label: string }[] = [
  { key: 'rel',  label: '推荐顺序' },
  { key: 'book', label: '按图书' },
  { key: 'page', label: '按页码' },
];

function sortResults(list: Result[], by: SortBy): Result[] {
  if (by === 'rel') return list;
  const arr = list.slice();
  if (by === 'book') {
    // 同书聚合：先按书名，再按页码，便于查看同一本书的多处命中
    arr.sort((a, b) =>
      (a.bookTitle ?? '').localeCompare(b.bookTitle ?? '') || (a.page ?? 1e9) - (b.page ?? 1e9));
  } else {
    // 按页码升序（缺页码的排末尾）
    arr.sort((a, b) => (a.page ?? 1e9) - (b.page ?? 1e9));
  }
  return arr;
}

export default function SearchPage() {
  const nav = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const voiceAttempt = useRef(0);
  const searchVersion = useRef(0);

  const [q, setQ] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [species, setSpecies] = useState<{ name: string; sub: string; icon: string; id?: string }[]>([]);
  const [speciesLoading, setSpeciesLoading] = useState(true);
  const [results, setResults] = useState<Result[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searching, setSearching] = useState(false);
  const [voiceState, setVoiceState] = useState<'idle' | 'starting' | 'listening' | 'stopping'>('idle');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('rel');
  const sortedResults = useMemo(() => sortResults(results, sortBy), [results, sortBy]);

  // 所有筛选项均由独立 JSON 字段提交；已有搜索结果时切换条件会立即重新检索。
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    category: '全部',
    region: '全部',
    production_area: '全部',
    waterway: '全部',
    feature: '全部',
    habit: '全部',
  });
  const activeFilters = Object.values(filters).filter((v) => v && v !== '全部');
  const shownSpecies = useMemo(
    () => (
      filters.category === '全部'
        ? species
        : species.filter((s) => normalizeCategory(s.sub) === filters.category)
    ),
    [species, filters.category],
  );
  // 本次检索命中的"相关品种"(图谱节点) 与"配图"。后端三路融合里的图谱召回 +
  // content.images 实体匹配会带回它们；单独成块展示，让搜索不只是"文本列表"。
  type ImageHit = { id: string; url: string; caption?: string };
  const [hitNodes, setHitNodes] = useState<SpeciesRef[]>([]);
  const [hitImages, setHitImages] = useState<ImageHit[]>([]);

  // 只展示接口返回的真实品种；服务不可用时保留空状态，不展示静态替代资料。
  useEffect(() => {
    let cancelled = false;
    speciesSearchApi('', 50)
      .then((data: { items?: SpeciesRef[] } | SpeciesRef[]) => {
        if (cancelled) return;
        const items: SpeciesRef[] = Array.isArray(data) ? data : (data.items ?? []);
        setSpecies(items.slice(0, 50).map((s) => ({
          id: s.id,
          name: s.name,
          sub: normalizeCategory(s.category ?? '—'),
          icon: pickEmoji(s.name),
        })));
      })
      .catch(() => { if (!cancelled) setSpecies([]); })
      .finally(() => { if (!cancelled) setSpeciesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    voiceAttempt.current += 1;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onstart = recognition.onresult = recognition.onerror = recognition.onend = null;
      recognition.abort();
    }
  }, []);

  // 滚动监听：hero 离开可视区时显示 sticky bar
  useEffect(() => {
    const hero = heroRef.current;
    const bar = stickyRef.current;
    if (!hero || !bar) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.intersectionRatio < 0.15) bar.classList.add('show');
        else bar.classList.remove('show');
      });
    }, { threshold: [0, 0.15, 0.3, 0.6, 1] });
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  async function runSearch(query: string, selectedFilters = filters, scrollToResults = true) {
    const trimmed = query.trim();
    if (!trimmed) return;
    const version = ++searchVersion.current;
    setSubmittedQuery(trimmed);
    setHasSearched(true);
    setSearchError(false);
    setSearching(true);
    const apiFilters: SearchFilters = Object.fromEntries(
      Object.entries(selectedFilters).filter(([, value]) => value && value !== '全部'),
    );
    try {
      const data = await searchApi(trimmed, apiFilters);
      if (version !== searchVersion.current) return;
      trackEvent('search_submit', {
        path: '/search',
        metadata: { query_length: trimmed.length, filter_count: Object.keys(apiFilters).length },
      });
      // 真实返回 0 条 → 展示空状态，不回退任何示意内容。
      setResults(toResultCards(data));
      setHitNodes(data.nodes ?? []);
      setHitImages(data.images ?? []);
      // 结果在下方，滚动过去给出明确反馈（否则从顶部 hero 点击像"没反应"）
      if (scrollToResults) {
        setTimeout(() => {
          if (version === searchVersion.current) resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }
    } catch {
      if (version !== searchVersion.current) return;
      setResults([]);
      setHitNodes([]);
      setHitImages([]);
      setSearchError(true);
      toast('暂时无法查找资料，请稍后重试');
    } finally {
      if (version === searchVersion.current) setSearching(false);
    }
  }

  function setFilter(key: FilterKey, value: string) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (hasSearched && q.trim()) void runSearch(q, next, false);
  }

  const onSubmitHero = () => runSearch(q);

  async function toggleVoiceSearch() {
    if (voiceState === 'starting' || voiceState === 'stopping') return;
    if (recognitionRef.current) {
      setVoiceState('stopping');
      recognitionRef.current.stop();
      return;
    }
    const attempt = ++voiceAttempt.current;
    setVoiceError(null);
    if (!window.isSecureContext) {
      setVoiceError('语音检索需要安全连接，请通过 HTTPS 域名打开本站后重试。');
      return;
    }
    const speechWindow = window as typeof window & {
      SpeechRecognition?: WebSpeechRecognitionConstructor;
      webkitSpeechRecognition?: WebSpeechRecognitionConstructor;
    };
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceError('当前浏览器不支持语音识别，请使用支持该功能的浏览器打开本站。');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('当前浏览器未提供麦克风访问能力，请使用支持录音的浏览器打开本站。');
      return;
    }

    const recognition = new Recognition();
    let finalTranscript = '';
    let canSearch = true;
    recognition.lang = 'zh-CN';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onstart = () => {
      if (voiceAttempt.current === attempt) setVoiceState('listening');
    };
    recognition.onresult = (event) => {
      if (voiceAttempt.current !== attempt) return;
      let transcript = '';
      let final = '';
      for (let index = 0; index < event.results.length; index += 1) {
        transcript += event.results[index][0]?.transcript ?? '';
        if (event.results[index].isFinal) final += event.results[index][0]?.transcript ?? '';
      }
      finalTranscript = final;
      setQ(transcript.trim());
    };
    recognition.onerror = async (event) => {
      canSearch = false;
      const permission = event.error === 'not-allowed' ? await readMicrophonePermission() : 'unknown';
      if (voiceAttempt.current !== attempt) return;
      setVoiceError(voiceErrorMessage(event.error, permission));
      // 不记录录音、识别文本或个人信息，仅保留排障所需的错误码和权限状态。
      if (event.error !== 'aborted') console.warn(`[voice-search] error=${event.error} microphone=${permission}`);
    };
    recognition.onend = () => {
      if (voiceAttempt.current !== attempt) return;
      recognitionRef.current = null;
      setVoiceState('idle');
      const query = finalTranscript.trim();
      if (canSearch && query) void runSearch(query);
    };
    recognitionRef.current = recognition;
    setVoiceState('starting');
    try {
      await requestMicrophoneAccess();
      if (voiceAttempt.current !== attempt) return;
      recognition.start();
    } catch (error) {
      if (voiceAttempt.current !== attempt) return;
      recognitionRef.current = null;
      setVoiceState('idle');
      const name = error instanceof Error ? error.name : 'UnknownError';
      const permission = await readMicrophonePermission();
      if (voiceAttempt.current !== attempt) return;
      setVoiceError(name === 'NotAllowedError' || name === 'SecurityError'
        ? voiceErrorMessage('not-allowed', permission)
        : name === 'NotFoundError'
          ? voiceErrorMessage('device-not-found')
          : name === 'NotReadableError'
            ? voiceErrorMessage('device-not-readable')
          : '语音识别未能启动，请稍后重试。');
      console.warn(`[voice-search] phase=microphone-request error=${name} microphone=${permission}`);
    }
  }

  return (
    <div className="container search-container">
      {/* 滚动后吸顶的紧凑搜索框 */}
      <div className="sticky-search" ref={stickyRef}>
        <span className="icon"><UiIcon name="search" /></span>
        <input
          aria-label="继续搜索"
          placeholder="继续搜索水产品种、图书知识…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (isSubmitEnter(e)) runSearch(q); }}
        />
        <div className="chips-quick">
          <button className="chip outline" type="button" onClick={() => filtersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>筛选条件</button>
        </div>
        <button className="go" onClick={onSubmitHero} disabled={searching}>{searching ? '查找中…' : '查找 ↗'}</button>
      </div>

      <div className="search-hero" ref={heroRef}>
        <div className="hero-grid"></div>
        <span className="kicker"><span className="dot"></span>有出处的水产知识</span>
        <h1>想了解鱼虾？<br/><span className="accent-word">问一句就知道</span></h1>
        <p className="lead">查品种、问区别，从图书资料中寻找有出处的答案。</p>
        <div className="search-wrap">
          <div className="search-box">
            <span className="icon"><UiIcon name="search" /></span>
            <input
              aria-label="搜索品种或问题"
              placeholder="输入品种名称或问题"
              enterKeyHint="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (isSubmitEnter(e)) runSearch(q); }}
            />
            <button
              className={`voice ${voiceState === 'listening' ? 'listening' : ''}`}
              type="button"
              aria-label={voiceState === 'starting' ? '正在启动语音检索' : voiceState === 'stopping' ? '正在停止语音检索' : voiceState === 'listening' ? '停止语音检索' : '开始语音检索'}
              aria-pressed={voiceState === 'listening'}
              title={voiceState === 'listening' ? '停止语音检索' : '语音检索'}
              onClick={toggleVoiceSearch}
              disabled={searching || voiceState === 'starting' || voiceState === 'stopping'}
            >
              <UiIcon name={voiceState === 'listening' ? 'stop' : 'mic'} />
            </button>
            <button className="go" onClick={onSubmitHero} disabled={searching}>{searching ? '查找中…' : '查找答案 ↗'}</button>
          </div>
          {voiceError && <p className="voice-feedback" role="alert">{voiceError}</p>}
        </div>
        <div className="search-meta">
          <span>已收录淡水鱼、海水鱼、虾蟹、贝类等多类资料</span>
        </div>
      </div>

      <div className="suggestions-wrap">
        <span className="lbl">大家在问</span>
        {SUGGESTED_QUESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            className="chip"
            onClick={() => { setQ(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="section-title">
        <h2>🐟 热门水产品种</h2>
        <span className="hint"><Link to="/atlas" className="chip outline sm">查看完整图鉴 →</Link></span>
      </div>
      <div className="hscroll">
        {speciesLoading ? (
          <div className="sub" style={{ padding: '12px 4px', color: 'var(--ink-3)' }}>正在加载已收录品种…</div>
        ) : shownSpecies.length === 0 ? (
          <div className="sub" style={{ padding: '12px 4px', color: 'var(--ink-3)' }}>该分类下暂无品种</div>
        ) : shownSpecies.map((s) => (
          <div
            key={(s.id ?? '') + s.name}
            className="species-card"
            role="link"
            tabIndex={0}
            onClick={() => nav(s.id ? `/species/${s.id}` : `/chat?q=${encodeURIComponent(s.name + ' 的分布和习性？')}`)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                nav(s.id ? `/species/${s.id}` : `/chat?q=${encodeURIComponent(s.name + ' 的分布和习性？')}`);
              }
            }}
          >
            <div className="img">{s.icon}</div>
            <div className="name">{s.name}</div>
            <div className="sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="section-title" ref={filtersRef} style={{ scrollMarginTop: 80 }}>
        <h2>🧭 按条件查找</h2>
        <span className="hint">可以组合选择品类、产地、水系和习性</span>
      </div>
      <div className="filter-row">
        {(Object.keys(SEARCH_FILTERS) as FilterKey[]).map((key) => (
          <FilterGroup
            key={key}
            groupKey={key}
            items={SEARCH_FILTERS[key]}
            value={filters[key]}
            onChange={(v) => setFilter(key, v)}
          />
        ))}
      </div>

      <div className="result-hint" ref={resultsRef} style={{ scrollMarginTop: 70 }}>
        <span className="left">
          {hasSearched
            ? <>🎯 共 {sortedResults.length} 条结果 · 按 {SORTS.find((s) => s.key === sortBy)?.label} 排序</>
            : '输入品种名称或问题，开始查找资料'}
          {activeFilters.length > 0 && <span style={{ color: 'var(--brand)' }}> · 筛选：{activeFilters.join(' / ')}</span>}
        </span>
        <span className="flex gap-8">
          {SORTS.map((s) => (
            <button type="button"
              key={s.key}
              className={`chip outline ${sortBy === s.key ? 'active' : ''}`}
              aria-pressed={sortBy === s.key}
              onClick={() => setSortBy(s.key)}
            >
              {s.label}
            </button>
          ))}
        </span>
      </div>

      {/* 命中品种 + 配图：仅搜索后且有图谱/图片命中时出现。点品种进入图谱详情。 */}
      {hasSearched && !searching && !searchError && (hitNodes.length > 0 || hitImages.length > 0) && (
        <details key={submittedQuery} className="card matched-block">
          <summary>相关品种与配图（按需查看）</summary>
          {hitNodes.length > 0 && (
            <div className="matched-species">
              <span className="lbl">🎯 相关品种</span>
              {hitNodes.map((n) => (
                <span
                  key={n.id}
                  className="chip"
                  title={n.category ?? ''}
                  onClick={() => nav(`/species/${n.id}`)}
                >
                  {pickEmoji(n.name)} {n.name}
                  {n.category ? <span className="sub-tag">{n.category}</span> : null}
                </span>
              ))}
            </div>
          )}
          {hitImages.length > 0 && (
            <div className="matched-images">
              {hitImages.map((im) => <SearchImage key={im.id} image={im} />)}
            </div>
          )}
        </details>
      )}

      {searching ? (
        <div className="card" role="status" style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--ink-2)' }}>
          正在查找资料…
        </div>
      ) : searchError ? (
        <div className="card" style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--ink-3)' }}>
          暂时无法查找资料，请稍后重试。
        </div>
      ) : hasSearched && sortedResults.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--ink-3)' }}>
          🪹 没有找到能匹配 "<b style={{ color: 'var(--ink-2)' }}>{submittedQuery}</b>" 的资料，不展示无关结果。可以换个关键词或调整筛选条件。
        </div>
      ) : (
        <div className="grid cols-2">
          {sortedResults.map((r) => (
            <div
              key={`${submittedQuery}:${r.id}`}
              className="card result-card"
            >
              {r.ribbon && <div className="ribbon">{r.ribbon}</div>}
              <div className="thumb">{r.thumb}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4>{r.title}</h4>
                <div className="meta">
                  {r.meta.map((m, j) => (
                    <Fragment key={j}>
                      <span>{m}</span>
                      {j < r.meta.length - 1 && <span className="dot">·</span>}
                    </Fragment>
                  ))}
                </div>
                <div className="excerpt-label">{r.focused ? '所问内容 · 原文摘录' : '相关原文摘要'}</div>
                <div className="excerpt"><ScientificText text={r.excerpt} sources={[r.original]} /></div>
                <details className="result-original">
                  <summary>查看完整原文</summary>
                  <div><ScientificText text={r.original} /></div>
                </details>
                <div className="foot">
                  {r.bookId ? (
                    <Link
                      to={`/book/${r.bookId}`}
                      className="cite"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.cite}
                    </Link>
                  ) : (
                    <span className="cite">{r.cite}</span>
                  )}
                  <Link to={`/chat?q=${encodeURIComponent(submittedQuery)}`} className="result-ask">继续问答</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="footer">©2026 上海渔联网科技 · 科普趣答</div>
    </div>
  );
}

// 单个筛选行：受控，选中值由父组件统一管理。
function FilterGroup({
  groupKey, items, value, onChange,
}: {
  groupKey: FilterKey;
  items: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const lbl = FILTER_LABELS[groupKey];
  return (
    <div className="group" role="group" aria-label={lbl.text}>
      <span className="label-inline">
        <span className="dot" style={{ background: lbl.color }}></span>
        {lbl.text}
      </span>
      <span className="chip-wrap">
        {items.map((x) => (
          <button type="button"
            key={x}
            className={`chip outline ${x === value ? 'active' : ''}`}
            aria-pressed={x === value}
            onClick={() => onChange(x)}
          >
            {x}
          </button>
        ))}
      </span>
    </div>
  );
}
